import type {
  CleanDataItem,
  InterventionalRadiologyCleanData,
  InterventionalRadiologySectionKey,
} from '../types/report';
import { makePlainTextSectionOrder } from './plainTextReport';

/**
 * What is specific to the plain-text ("letter") rendering of the
 * radiologie_interventionnelle report (ALDO-2650): the KB version gates, the
 * section catalog, and the Prévention flattening its ordering needs. The line
 * primitives shared with the other letters (ordering, capitalisation, score
 * interpretation, pastille) live in `plainTextReport.ts` — import them from
 * there rather than through this module.
 */

/**
 * KB 58.3 is the last version released before the plain-text RI report
 * shipped. Reports generated on it or anything older keep the card template so
 * already-generated reports don't change shape retroactively.
 */
export const RI_PLAIN_TEXT_MIN_EXCLUSIVE_KB_VERSION = '58.3';

const compareNumericSegments = (a: string[], b: string[]): number => {
  const length = Math.max(a.length, b.length);
  for (let i = 0; i < length; i++) {
    // A missing segment counts as 0 so "58" === "58.0".
    const segA = a[i] === undefined || a[i] === '' ? 0 : Number(a[i]);
    const segB = b[i] === undefined || b[i] === '' ? 0 : Number(b[i]);
    const numA = Number.isNaN(segA) ? 0 : segA;
    const numB = Number.isNaN(segB) ? 0 : segB;
    if (numA !== numB) return numA - numB;
  }
  return 0;
};

/**
 * Segment-wise numeric comparison of dotted KB versions. The
 * `Number(version) >= X` idiom used elsewhere is NOT safe here:
 * `Number('58.10')` is 58.1, which would sort below 58.3.
 */
export const compareKbVersions = (a: string, b: string): number =>
  compareNumericSegments(a.split('.'), b.split('.'));

export const isRiPlainTextReportEligible = (
  version: string | null | undefined,
): boolean =>
  !!version &&
  compareKbVersions(version, RI_PLAIN_TEXT_MIN_EXCLUSIVE_KB_VERSION) > 0;

/**
 * KB 57.6 is the first version whose RI reports carry the structured report
 * spans the card template renders; 57.5 and older have none and fall back to
 * QUESTIONS_ANSWERS. Uses the same dotted-segment compare as the plain-text
 * gate (so a hypothetical 57.10 counts as ≥ 57.6, unlike `Number('57.10')`).
 */
export const RI_CARD_MIN_INCLUSIVE_KB_VERSION = '57.6';

export const isRiCardReportEligible = (
  version: string | null | undefined,
): boolean =>
  !!version &&
  compareKbVersions(version, RI_CARD_MIN_INCLUSIVE_KB_VERSION) >= 0;

export type RiPlainTextBodySectionKey = Exclude<
  InterventionalRadiologySectionKey,
  'Alertes' | 'Contexte'
>;

/**
 * The classification-ordered body sections of the letter. Alertes stays pinned
 * above the letter in its alert component, and Contexte is rendered first
 * (outside this order), so neither appears here. This is the catalog; the
 * display order for these comes from getRiPlainTextSectionOrder.
 */
export const RI_PLAIN_TEXT_BODY_SECTIONS: RiPlainTextBodySectionKey[] = [
  'Motifs de consultation',
  'Informations complémentaires',
  'Prévention',
  'Allergies et intolérances',
  'Traitements',
  'Antécédents personnels',
  'Antécédents chirurgicaux',
  'Santé de la femme',
  'Antécédents familiaux',
  'Scores',
];

/**
 * Prévention is the one nested section: its four sub-blocks are flattened so
 * the section's minimum classification is taken across all of them.
 */
const sectionTopLevelItems = (
  cleanData: InterventionalRadiologyCleanData,
  key: RiPlainTextBodySectionKey,
): CleanDataItem[] => {
  if (key === 'Prévention') {
    const prevention = cleanData.Prévention;
    if (!prevention) return [];
    return Object.values(prevention).flatMap((items) => items ?? []);
  }
  return cleanData[key] ?? [];
};

/** Letter body order for the RI report — see makePlainTextSectionOrder. */
export const getRiPlainTextSectionOrder = makePlainTextSectionOrder<
  RiPlainTextBodySectionKey,
  InterventionalRadiologyCleanData
>(RI_PLAIN_TEXT_BODY_SECTIONS, sectionTopLevelItems);
